import { PopupDialog } from "../lib/popup-dialog.ts";

type ConsentCategory = "marketing" | "analytics" | "preferences" | "sale_of_data";

interface CookieBannerRefs {
  overlay?: HTMLElement;

  dialog?: HTMLElement;

  settings?: HTMLElement;

  decline?: HTMLButtonElement;

  settingsToggle?: HTMLButtonElement;

  agree?: HTMLButtonElement;

  agreeLabel?: HTMLElement;

  categoryToggles?: HTMLInputElement[];
}

const HASH_TRIGGER = "#cookie-settings";

const CATEGORIES: ConsentCategory[] = ["marketing", "analytics", "preferences", "sale_of_data"];

/**
 * Custom cookie consent banner driven by the Shopify Customer Privacy API.
 *
 * Visibility:
 *   - On load, defers to Shopify's built-in banner if it is active
 *     (`window.privacyBanner` is defined).
 *   - Otherwise shows when `customerPrivacy.shouldShowBanner()` returns true
 *     and no consent values have been recorded yet.
 *   - Can always be re-opened by clicking any element with the
 *     `[data-cookie-banner-trigger]` attribute (used by the footer
 *     "Cookie settings" button), or by navigating to a URL with the
 *     `#cookie-settings` hash (for use in merchant-authored inline
 *     content where adding data attributes is impractical).
 */
class CookieBanner extends PopupDialog<CookieBannerRefs> {
  #defaultPrimaryLabel = "";

  get inertId(): string {
    return "cookie-banner";
  }

  // Sit above other popup dialogs so the cookie banner always wins when
  // multiple are open simultaneously.
  get inertPriority(): number {
    return 1;
  }

  getFocusTarget(): HTMLElement | null {
    if (this.#settingsExpanded) return this.refs.dialog;
    return this.refs.agree ?? null;
  }

  onEscape(): void {
    this.#handleDecline();
  }

  connectedCallback(): void {
    super.connectedCallback();

    this.#defaultPrimaryLabel = this.refs.agreeLabel?.textContent ?? "";

    this.refs.overlay.addEventListener("click", this.#handleDecline, { signal: this.signal });
    this.refs.decline?.addEventListener("click", this.#handleDecline, { signal: this.signal });
    this.refs.agree?.addEventListener("click", this.#handlePrimary, { signal: this.signal });
    this.refs.settingsToggle?.addEventListener("click", this.#toggleSettings, {
      signal: this.signal,
    });
    document.addEventListener("click", this.#handleTriggerClick, { signal: this.signal });
    window.addEventListener("hashchange", this.#handleHashTrigger, { signal: this.signal });
    this.#handleHashTrigger();

    this.#whenApiReady(() => {
      this.#applyRegionalVisibility();
      if (this.#shopifyBannerActive()) return;
      const api = this.#api();
      if (!api) return;
      // shouldShowBanner() is supposed to flip to false once consent is
      // recorded, but in practice it stays true after a programmatic
      // setTrackingConsent. Treat any non-empty consent value as "already
      // responded" so we don't re-show the banner on every page load.
      if (this.#hasRespondedToConsent()) return;
      if (window.Shopify?.designMode) return;
      if (api.shouldShowBanner()) {
        this.open();
      }
    });
  }

  open(options: { forceSettings?: boolean } = {}): void {
    if (this.isOpen) return;
    if (options.forceSettings && !this.#settingsExpanded) {
      this.#toggleSettings();
    }
    super.open();
  }

  #hasRespondedToConsent(): boolean {
    const api = this.#api();
    const consent = api?.currentVisitorConsent?.() ?? {};
    return Object.values(consent).some((value) => value === "yes" || value === "no");
  }

  #applyRegionalVisibility(): void {
    const api = this.#api();
    if (!api) return;
    // Only show the "sale of data" toggle in CCPA-style regions where it
    // applies, matching Shopify's built-in banner behavior.
    if (api.saleOfDataRegion?.() === true) return;
    const toggles = this.refs.categoryToggles ?? [];
    for (const toggle of toggles) {
      if (toggle.dataset.category !== "sale_of_data") continue;
      const row = toggle.parentElement;
      if (row instanceof HTMLElement) row.hidden = true;
    }
  }

  #api(): CustomerPrivacyApi | undefined {
    return window.Shopify?.customerPrivacy;
  }

  #shopifyBannerActive(): boolean {
    return typeof window.privacyBanner !== "undefined";
  }

  #whenApiReady(callback: () => void): void {
    let fired = false;
    const fire = () => {
      if (fired || !this.#api()) return;
      fired = true;
      callback();
    };

    if (this.#api()) {
      fire();
      return;
    }

    document.addEventListener("shopifyCustomerPrivacyApiLoaded", fire, { signal: this.signal });

    const tryLoad = () => {
      if (fired) return;
      if (this.#api()) {
        fire();
        return;
      }
      const loadFeatures = window.Shopify?.loadFeatures;
      if (typeof loadFeatures === "function") {
        loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], fire);
      }
    };

    tryLoad();
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", tryLoad, { signal: this.signal });
    }
    if (document.readyState !== "complete") {
      window.addEventListener("load", tryLoad, { signal: this.signal });
    }
  }

  #handleTriggerClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest("[data-cookie-banner-trigger]")) {
      event.preventDefault();
      this.#whenApiReady(() => this.open({ forceSettings: true }));
      return;
    }
    if (!this.#isSameDocumentHashTriggerClick(event)) return;
    event.preventDefault();
    this.#whenApiReady(() => this.open({ forceSettings: true }));
  };

  /**
   * Intercept clicks on `<a href="#cookie-settings">` (same document) so the
   * URL hash never changes — otherwise other hash-driven components (e.g.
   * modal-panel) would close in response to the navigation.
   */
  #isSameDocumentHashTriggerClick(event: MouseEvent): boolean {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (!(event.target instanceof Element)) return false;
    const link = event.target.closest("a[href]");
    if (!(link instanceof HTMLAnchorElement)) return false;
    if (link.target && link.target !== "_self") return false;
    if (link.hash !== HASH_TRIGGER) return false;
    return (
      link.origin === location.origin &&
      link.pathname === location.pathname &&
      link.search === location.search
    );
  }

  #handleHashTrigger = (): void => {
    if (window.location.hash !== HASH_TRIGGER) return;
    history.replaceState(null, "", window.location.pathname + window.location.search);
    this.#whenApiReady(() => this.open({ forceSettings: true }));
  };

  #handleDecline = (): void => {
    this.#setConsent({
      marketing: false,
      analytics: false,
      preferences: false,
      sale_of_data: false,
    });
  };

  #handlePrimary = (): void => {
    if (this.#settingsExpanded) {
      const consent: Record<ConsentCategory, boolean> = {
        marketing: false,
        analytics: false,
        preferences: false,
        sale_of_data: false,
      };
      const toggles = this.refs.categoryToggles ?? [];
      for (const toggle of toggles) {
        const category = toggle.dataset.category;
        if (isConsentCategory(category)) {
          consent[category] = toggle.checked;
        }
      }
      this.#setConsent(consent);
    } else {
      // sale_of_data is only meaningful in CCPA-style regions where the
      // toggle is visible. Outside those regions the user was never asked,
      // so accepting the visible categories must not opt them in.
      const inSaleOfDataRegion = this.#api()?.saleOfDataRegion?.() === true;
      this.#setConsent({
        marketing: true,
        analytics: true,
        preferences: true,
        sale_of_data: inSaleOfDataRegion,
      });
    }
  };

  get #settingsExpanded(): boolean {
    return Boolean(this.refs.settings && !this.refs.settings.hasAttribute("hidden"));
  }

  #toggleSettings = (): void => {
    if (!this.refs.settings) return;
    const expanded = this.#settingsExpanded;
    if (expanded) {
      this.refs.settings.setAttribute("hidden", "");
      this.refs.settingsToggle?.setAttribute("aria-expanded", "false");
      if (this.refs.agreeLabel) this.refs.agreeLabel.textContent = this.#defaultPrimaryLabel;
    } else {
      this.#syncTogglesFromConsent();
      this.refs.settings.removeAttribute("hidden");
      this.refs.settingsToggle?.setAttribute("aria-expanded", "true");
      const saveLabel = this.#getSaveLabel();
      if (saveLabel && this.refs.agreeLabel) this.refs.agreeLabel.textContent = saveLabel;
    }
  };

  #getSaveLabel(): string | null {
    return this.dataset.saveLabel || null;
  }

  #syncTogglesFromConsent(): void {
    const api = this.#api();
    if (!api) return;
    const current = api.currentVisitorConsent?.() ?? {};
    const toggles = this.refs.categoryToggles ?? [];
    for (const toggle of toggles) {
      const category = toggle.dataset.category;
      if (!category) continue;
      const value = current[category];
      toggle.checked = value === "yes";
    }
  }

  #setConsent(consent: Record<ConsentCategory, boolean>): void {
    const api = this.#api();
    if (!api) {
      this.close();
      return;
    }
    let closed = false;
    const closeOnce = () => {
      if (closed) return;
      closed = true;
      this.close();
    };
    // Close on the API callback if it fires, but never block the user — if
    // the consent write hangs or fails (e.g. CORS in `shopify theme dev`),
    // dismiss the modal anyway so they aren't stuck.
    const fallback = setTimeout(closeOnce, 1500);
    api.setTrackingConsent(consent, () => {
      clearTimeout(fallback);
      closeOnce();
    });
  }
}

const CATEGORY_SET: ReadonlySet<string> = new Set(CATEGORIES);

function isConsentCategory(value: string | undefined): value is ConsentCategory {
  return value !== undefined && CATEGORY_SET.has(value);
}

if (!customElements.get("cookie-banner")) {
  customElements.define("cookie-banner", CookieBanner);
}
